import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import GlassPanel from "@/components/GlassPanel";
import api from "@/lib/api";
import {
  ArrowLeft, RefreshCw, Trophy, MapPin, Calendar,
  ArrowUp, ArrowDown, Minus, Download,
  Maximize2, Minimize2,
} from "lucide-react";
import EventLeaderboardShareCard from "@/components/EventLeaderboardShareCard";
import useDownloadEventLeaderboard from "@/lib/useDownloadEventLeaderboard";
import { PODIUM } from "@/lib/podium";
import { LeaderboardSearchBar, matchesLeaderboardFilters } from "@/components/LeaderboardSearchBar";

const PILLARS = [
  { key: "strength",  label: "STR" },
  { key: "speed",     label: "SPD" },
  { key: "explosive", label: "EXP" },
  { key: "stamina",   label: "STA" },
  { key: "control",   label: "CTL" },
];

// Poll every 30 seconds for a "live" feel.
const POLL_MS = 30_000;

const STATUS_LABEL = { live: "● LIVE", past: "● PAST", upcoming: "UPCOMING" };

// Renders the movement indicator vs previous-poll rank.
function RankMovement({ current, prev }) {
  if (prev == null || prev === current) {
    return (
      <span
        className="inline-flex items-center gap-1 text-mute gps-mono text-[10px]"
        data-testid={`rank-move-${current}`}
        data-direction="same"
        title={prev == null ? "New entry" : "No change"}
      >
        <Minus size={10} />
        {prev == null ? "NEW" : "—"}
      </span>
    );
  }
  const delta = prev - current; // positive = moved up
  const up = delta > 0;
  return (
    <span
      className={`inline-flex items-center gap-1 gps-mono text-[10px] font-semibold ${
        up ? "text-emerald-400" : "text-rose-400"
      }`}
      data-testid={`rank-move-${current}`}
      data-direction={up ? "up" : "down"}
      title={`Moved ${up ? "up" : "down"} ${Math.abs(delta)} place${Math.abs(delta) === 1 ? "" : "s"}`}
    >
      {up ? <ArrowUp size={10} /> : <ArrowDown size={10} />}
      {Math.abs(delta)}
    </span>
  );
}

export default function EventLeaderboard() {
  const { eventId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const timerRef = useRef(null);
  // Snapshot of previous poll's ranks keyed by user_id, used for arrow deltas.
  const prevRanksRef = useRef({});
  const dl = useDownloadEventLeaderboard();
  const [sortKey, setSortKey] = useState("gps");
  const fsRef = useRef(null);
  const [isFullscreen, setIsFullscreen] = useState(false);

  // Keep local state in sync when the user exits fullscreen via ESC or the
  // browser chrome (so the icon flips back correctly).
  useEffect(() => {
    const sync = () => setIsFullscreen(Boolean(document.fullscreenElement));
    document.addEventListener("fullscreenchange", sync);
    return () => document.removeEventListener("fullscreenchange", sync);
  }, []);

  const toggleFullscreen = async () => {
    try {
      if (document.fullscreenElement) {
        await document.exitFullscreen();
      } else if (fsRef.current?.requestFullscreen) {
        await fsRef.current.requestFullscreen();
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("Fullscreen toggle failed:", err);
    }
  };

  const load = async ({ silent = false } = {}) => {
    if (silent) setRefreshing(true);
    try {
      const { data: res } = await api.get(`/events/${eventId}/leaderboard`, {
        params: { sort: sortKey },
      });
      setData(res);
      setLastUpdated(new Date());
      setError(null);
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  // Initial load + 30s background poll. Restart interval on eventId OR sort change.
  useEffect(() => {
    prevRanksRef.current = {};
    load();
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => load({ silent: true }), POLL_MS);
    return () => clearInterval(timerRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId, sortKey]);

  const event = data?.event;

  // Attach the previous rank to each entry, and update the ref for the next tick.
  const entries = useMemo(() => {
    const prev = prevRanksRef.current;
    const genderCount = {};
    const rows = (data?.entries || []).map((e) => {
      const g = (e.gender || "").charAt(0).toUpperCase();
      genderCount[g] = (genderCount[g] || 0) + 1;
      return {
        ...e,
        genderRank: genderCount[g],
        prevRank: prev[e.user_id]?.rank ?? null,
        prevGenderRank: prev[e.user_id]?.genderRank ?? null,
      };
    });
    // Update snapshot AFTER we compute prevRank for this render.
    const nextMap = {};
    rows.forEach((e) => { nextMap[e.user_id] = { rank: e.rank, genderRank: e.genderRank }; });
    prevRanksRef.current = nextMap;
    return rows;
  }, [data]);

  const [query, setQuery] = useState("");
  const [gender, setGender] = useState("");
  const visibleEntries = useMemo(
    () => entries
      .filter((e) => matchesLeaderboardFilters(e, "", gender))
      .map((e) => (gender ? { ...e, rank: e.genderRank, prevRank: e.prevGenderRank } : e))
      .filter((e) => matchesLeaderboardFilters(e, query, "")),
    [entries, query, gender]
  );

  return (
    <div
      ref={fsRef}
      className={`max-w-7xl mx-auto px-6 md:px-12 py-14 ${
        isFullscreen ? "!max-w-none w-screen h-screen overflow-y-auto bg-[#05070c] py-8 md:px-16" : ""
      }`}
      data-testid="event-leaderboard-page"
    >
      <div className="mb-6">
        <Link
          to="/events"
          className="text-mute hover:text-white gps-mono text-[10px] tracking-[0.25em] inline-flex items-center gap-2"
          data-testid="event-leaderboard-back"
        >
          <ArrowLeft size={12} /> BACK TO EVENTS
        </Link>
      </div>

      {loading && (
        <div className="text-mute gps-mono text-xs tracking-[0.2em]" data-testid="event-leaderboard-loading">
          LOADING LEADERBOARD…
        </div>
      )}

      {error && !loading && (
        <GlassPanel className="p-8" data-testid="event-leaderboard-error">
          <div className="gps-heading text-2xl mb-2">EVENT NOT FOUND</div>
          <p className="text-mute text-sm">{error}</p>
        </GlassPanel>
      )}

      {event && !error && (
        <>
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <div
                className={`gps-tag mb-3 ${event.status === "live" ? "text-cyan animate-pulse" : ""}`}
                data-testid="event-leaderboard-status"
              >
                {STATUS_LABEL[event.status] || "EVENT"} · LEADERBOARD
              </div>
              <h1 className="gps-heading text-4xl md:text-5xl" data-testid="event-leaderboard-name">
                {event.name}
              </h1>
              <div className="flex items-center gap-4 text-mute text-xs gps-mono mt-4">
                <span className="flex items-center gap-2"><Calendar size={12} /> {event.date}</span>
                <span className="flex items-center gap-2"><MapPin size={12} /> {event.location}</span>
              </div>
            </div>
            <div className="text-right">
              <div className="inline-flex gap-2 flex-wrap justify-end">
                <button
                  onClick={() => dl.download(eventId, { snapshot: data, fetchFresh: false, gender })}
                  className="gps-btn ghost !px-2"
                  disabled={dl.busyEventId === eventId || !data}
                  data-testid="event-leaderboard-download"
                  title="Download shareable PNG"
                  aria-label="Download PNG"
                >
                  <Download size={14} />
                </button>
                <button
                  onClick={toggleFullscreen}
                  className="gps-btn ghost !px-2"
                  data-testid="event-leaderboard-fullscreen"
                  title={isFullscreen ? "Exit fullscreen" : "Fullscreen for room display"}
                  aria-label={isFullscreen ? "Exit fullscreen" : "Enter fullscreen"}
                  aria-pressed={isFullscreen}
                >
                  {isFullscreen ? <Minimize2 size={14} /> : <Maximize2 size={14} />}
                </button>
                <button
                  onClick={() => load({ silent: true })}
                  className="gps-btn ghost !px-2"
                  disabled={refreshing}
                  data-testid="event-leaderboard-refresh"
                  title="Refresh now"
                  aria-label="Refresh"
                >
                  <RefreshCw size={14} className={refreshing ? "animate-spin" : ""} />
                </button>
              </div>
              <div
                className="mt-2 gps-mono text-[10px] text-mute tracking-[0.2em]"
                data-testid="event-leaderboard-updated"
              >
                {lastUpdated
                  ? `UPDATED · ${lastUpdated.toLocaleTimeString()} · AUTO 30s`
                  : ""}
              </div>
            </div>
          </div>

          <div className="mt-8">
            <LeaderboardSearchBar query={query} onQuery={setQuery} gender={gender} onGender={setGender} prefix="event-lb" />
          </div>

          <GlassPanel className="mt-4 overflow-x-auto" data-testid="event-leaderboard-table">
            <table className="w-full text-sm min-w-[1120px] border-collapse">
              <thead>
                <tr className="border-b border-white/15">
                  <th className="p-3 text-left gps-label">RANK</th>
                  <th className="p-3 text-left gps-label">MOVE</th>
                  <th className="p-3 text-left gps-label">ID</th>
                  <th className="p-3 text-left gps-label">NAME</th>
                  <th className="p-3 text-left gps-label">INSTITUTION</th>
                  <th className="p-3 text-left gps-label">GENDER</th>
                  <th className="p-3 text-left gps-label">ARCHETYPE</th>
                  {PILLARS.map((p) => (
                    <th
                      key={p.key}
                      onClick={() => setSortKey(p.key)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(ev) => (ev.key === "Enter" || ev.key === " ") && setSortKey(p.key)}
                      aria-sort={sortKey === p.key ? "descending" : "none"}
                      className={`p-3 text-right gps-label cursor-pointer select-none hover:text-cyan-200 ${
                        sortKey === p.key ? "text-cyan-300" : ""
                      }`}
                      data-testid={`event-lb-sort-${p.key}`}
                      title={`Sort by ${p.label}`}
                    >
                      <span className="inline-flex items-center gap-1 justify-end">
                        {p.label}
                        {sortKey === p.key && <ArrowDown size={10} />}
                      </span>
                    </th>
                  ))}
                  <th
                    onClick={() => setSortKey("gps")}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(ev) => (ev.key === "Enter" || ev.key === " ") && setSortKey("gps")}
                    aria-sort={sortKey === "gps" ? "descending" : "none"}
                    className={`p-3 text-right gps-label cursor-pointer select-none hover:text-cyan-200 ${
                      sortKey === "gps" ? "text-cyan-300" : ""
                    }`}
                    data-testid="event-lb-sort-gps"
                    title="Sort by GPS overall"
                  >
                    <span className="inline-flex items-center gap-2 justify-end">
                      <Trophy size={12} /> GPS
                      {sortKey === "gps" && <ArrowDown size={10} />}
                    </span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {visibleEntries.map((e) => {
                  const podium = PODIUM[e.rank];
                  return (
                    <tr
                      key={e.user_id}
                      className={`border-b border-white/5 transition-colors ${
                        podium ? `${podium.rowBg} ${podium.ring}` : "hover:bg-white/5"
                      }`}
                      data-testid={`event-lb-row-${e.rank}`}
                      data-podium={podium ? podium.label.toLowerCase() : undefined}
                    >
                      <td className="p-3 big-numeric whitespace-nowrap">
                        <span className={podium?.accentText || "text-white"}>{e.rank}</span>
                        {podium && <span className="ml-2 inline-block align-middle">{podium.icon}</span>}
                      </td>
                      <td className="p-3">
                        <RankMovement current={e.rank} prev={e.prevRank} />
                      </td>
                      <td className="p-3 gps-mono text-xs text-cyan-400 tracking-[0.2em]">
                        {e.public_id || "—"}
                      </td>
                      <td className={`p-3 ${podium ? "font-semibold text-white" : ""}`}>
                        {e.name || "—"}
                      </td>
                      <td className="p-3 gps-mono text-xs text-mute max-w-[200px] truncate" title={e.gym || ""} data-testid={`event-lb-gym-${e.rank}`}>{e.gym || "—"}</td>
                      <td className="p-3 gps-mono text-xs text-mute">{e.gender || "—"}</td>
                      <td
                        className="p-3 gps-mono text-xs"
                        data-testid={`event-lb-archetype-${e.rank}`}
                        title={e.archetype?.code ? `${e.archetype.code} · ${e.archetype.tag || ""}`.trim() : ""}
                      >
                        {e.archetype?.name ? (
                          <span className="inline-flex flex-col leading-tight">
                            <span className="text-white">{e.archetype.name.toUpperCase()}</span>
                            {e.archetype.tag && (
                              <span className="text-[9px] text-cyan-400 tracking-[0.15em]">
                                {e.archetype.tag.toUpperCase()}
                              </span>
                            )}
                          </span>
                        ) : (
                          <span className="text-mute">—</span>
                        )}
                      </td>
                      {PILLARS.map((p) => (
                        <td
                          key={p.key}
                          className={`p-3 gps-mono text-xs text-right ${
                            sortKey === p.key ? "text-cyan-300 font-semibold" : "text-white"
                          }`}
                          data-testid={`event-lb-${p.key}-${e.rank}`}
                        >
                          {e.pillars?.[p.key] != null ? Number(e.pillars[p.key]).toFixed(1) : "—"}
                        </td>
                      ))}
                      <td
                        className={`p-3 big-numeric text-right ${
                          sortKey === "gps" ? "text-cyan-300 font-semibold" : (podium?.accentText || "text-white")
                        }`}
                        data-testid={`event-lb-gps-${e.rank}`}
                      >
                        {e.gps != null ? Number(e.gps).toFixed(1) : "—"}
                      </td>
                    </tr>
                  );
                })}
                {visibleEntries.length === 0 && (
                  <tr>
                    <td colSpan={8 + PILLARS.length} className="p-10 text-center text-mute gps-mono text-xs tracking-[0.2em]" data-testid="event-lb-empty">
                      {entries.length === 0 ? "NO SCORES SUBMITTED YET · CHECK BACK SOON" : "NO ATHLETES MATCH FILTERS"}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </GlassPanel>
        </>
      )}

      {/* Hidden share-card render target used by the DOWNLOAD PNG action. */}
      {dl.payload && (
        <div style={{ position: "fixed", top: -100000, left: -100000, pointerEvents: "none" }} aria-hidden="true">
          <EventLeaderboardShareCard
            ref={dl.nodeRef}
            event={dl.payload.event}
            entries={dl.payload.entries}
            gender={dl.payload.gender}
          />
        </div>
      )}
    </div>
  );
}
